import {
  isSameDay,
  isSameYear,
  subDays,
  fromUnixTime,
  formatDistanceToNow,
  differenceInDays,
} from 'date-fns';
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz';
import zhCN from 'date-fns/locale/zh-CN';

// Borderchat serves European customers: every displayed date and time uses
// Central European time (with summer time), whatever zone the agent's computer
// is set to. Settable for tests.
export const DEFAULT_DISPLAY_TIME_ZONE = 'Europe/Rome';
let displayTimeZone = DEFAULT_DISPLAY_TIME_ZONE;
export const getDisplayTimeZone = () => displayTimeZone;
export const setDisplayTimeZone = zone => {
  displayTimeZone = zone || DEFAULT_DISPLAY_TIME_ZONE;
};

const format = (date, pattern, options) =>
  formatInTimeZone(date, displayTimeZone, pattern, options);
// Calendar comparisons (today, yesterday, this year) on the display zone's clock.
const zoned = date => utcToZonedTime(date, displayTimeZone);

/**
 * Formats a date with a date-fns pattern, in the display time zone.
 * @param {Date|string|number} value - A date or anything Date accepts.
 * @param {string} pattern - date-fns format pattern.
 * @returns {string} Formatted date string.
 */
export const formatDisplayDate = (value, pattern) =>
  format(new Date(value), pattern);

/**
 * Formats a date like Date#toLocaleString, in the display time zone.
 * @param {Date|string|number} value - A date or anything Date accepts.
 * @param {Intl.DateTimeFormatOptions} [options] - Extra formatting options.
 * @returns {string} Formatted date string.
 */
export const displayDateTime = (value, options = {}) =>
  new Date(value).toLocaleString(undefined, {
    ...options,
    timeZone: displayTimeZone,
  });

const dateFnsLocaleFor = locale =>
  String(locale || '')
    .replace('_', '-')
    .toLowerCase()
    .startsWith('zh-cn')
    ? zhCN
    : undefined;

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'h:mm a') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, dateFormat);
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (
  time,
  dateFormat = 'MMM d, yyyy',
  locale
) => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  const options = { locale: dateFnsLocaleFor(locale) };
  const messageDate = format(messageTime, dateFormat, options);
  if (!isSameYear(zoned(messageTime), zoned(now))) {
    return format(messageTime, 'LLL d y, h:mm a', options);
  }
  return messageDate;
};

/**
 * Formats a Unix timestamp relative to today: the time for today, a caller-
 * supplied label for yesterday, and a date otherwise. The yesterday label is
 * passed in so the caller keeps ownership of translation.
 * @param {number} time - Unix timestamp.
 * @param {string} yesterdayLabel - Localized label shown for yesterday.
 * @returns {string} Formatted timestamp string.
 */
export const relativeDayTimestamp = (time, yesterdayLabel) => {
  const date = fromUnixTime(time);
  const day = zoned(date);
  const today = zoned(new Date());
  if (isSameDay(day, today)) return format(date, 'h:mm a');
  if (isSameDay(day, subDays(today, 1))) return yesterdayLabel;
  if (isSameYear(day, today)) return format(date, 'MMM d');
  return format(date, 'MMM d, yyyy');
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = (time, locale) => {
  const unixTime = fromUnixTime(time);
  return formatDistanceToNow(unixTime, {
    addSuffix: true,
    locale: dateFnsLocaleFor(locale),
  });
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'MMM d, yyyy') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, df);
};

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false) => {
  // This function takes a time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "ago" to the end of the time string
  const suffix = withAgo ? ' ago' : '';
  const timeMappings = {
    'less than a minute ago': 'now',
    'in less than a minute': 'now',
    'a minute ago': `1m${suffix}`,
    'an hour ago': `1h${suffix}`,
    'a day ago': `1d${suffix}`,
    'a month ago': `1mo${suffix}`,
    'a year ago': `1y${suffix}`,
  };
  // Check if the time string is one of the specific cases
  if (timeMappings[time]) {
    return timeMappings[time];
  }
  const convertToShortTime = time
    .replace(/about|over|almost|/g, '')
    .replace(' minute ago', `m${suffix}`)
    .replace(' minutes ago', `m${suffix}`)
    .replace(' hour ago', `h${suffix}`)
    .replace(' hours ago', `h${suffix}`)
    .replace(' day ago', `d${suffix}`)
    .replace(' days ago', `d${suffix}`)
    .replace(' month ago', `mo${suffix}`)
    .replace(' months ago', `mo${suffix}`)
    .replace(' year ago', `y${suffix}`)
    .replace(' years ago', `y${suffix}`);
  return convertToShortTime;
};

/**
 * Formats a duration in seconds into mm:ss or hh:mm:ss.
 * @param {number|string} durationInSeconds - Duration in seconds.
 * @returns {string} Formatted duration string. Empty string for invalid input.
 */
export const formatDuration = durationInSeconds => {
  if (durationInSeconds === null || durationInSeconds === undefined) return '';

  const totalSeconds = Number(durationInSeconds);
  if (Number.isNaN(totalSeconds) || totalSeconds < 0) return '';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const mm = minutes.toString().padStart(2, '0');
  const ss = seconds.toString().padStart(2, '0');
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
